import apiClient from './apiClient';

export interface AuditLog {
    id: string;
    residentName?: string;
    entityName: string;
    entityId: string;
    action: string;
    actionTimestamp: string;
    userId: string;
    userName: string;
    userEmail: string;
    userRole: string;
    ipAddress: string;
    userAgent: string;
    endpoint: string;
    httpMethod: string;
    success: boolean;
    tenantId: string;
    facilityId: string;
    facilityName: string;
    clientId: string;
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
    deleted: boolean;
}
export interface AuditLogParams {
    page?: number;
    size?: number;
    search?: string;
    fromDate?: string;
    toDate?: string;
    sort?: string;
}

export interface PaginatedAuditLogs {
    content: AuditLog[];
    totalPages: number;
    totalElements: number;
    auditLogCount?: number;
    todayAuditLogCount?: number;
    failedAuditLogCount?: number;
}

const auditLogService = {
    getAll: async (params: AuditLogParams): Promise<PaginatedAuditLogs> => {
        const { page = 0, size = 30, fromDate, toDate, search, sort } = params;
        let url = `/v1/audit-logs?page=${page}&size=${size}`;
        
        if (search) {
            url = `/v1/audit-logs/search?search=${encodeURIComponent(search)}&page=${page}&size=${size}`;
        }
        
        if (fromDate) {
            url += `&fromDate=${fromDate}`;
        }
        if (toDate) {
            url += `&toDate=${toDate}`;
        }
        if (sort) {
            url += `&sort=${sort}`;
        }

        const response = await apiClient.get<any>(url);
        
        if (response.data && Array.isArray(response.data.content)) {
            return {
                content: response.data.content,
                totalPages: response.data.totalPages || 1,
                totalElements: response.data.totalElements || response.data.auditLogCount || response.data.content.length,
                auditLogCount: response.data.auditLogCount,
                todayAuditLogCount: response.data.todayAuditLogCount ?? response.data.auditLogCount,
                failedAuditLogCount: response.data.failedAuditLogCount,
            };
        } else if (Array.isArray(response.data)) {
            return {
                content: response.data,
                totalPages: Math.ceil(response.data.length / size),
                totalElements: response.data.length,
            };
        }
        
        return { content: [], totalPages: 1, totalElements: 0 };
    },

    /**
     * Calls the backend /v1/audit-logs/export endpoint which returns a
     * CSV / plain-text file directly from the server.
     * The response is returned as a Blob so the caller can trigger a download.
     */
    exportLogs: async (): Promise<Blob> => {
        const response = await apiClient.get('/v1/audit-logs/export', {
            headers: {
                Accept: 'application/json, text/plain, */*',
            },
            responseType: 'blob',
        });
        return response.data as Blob;
    },
};

export default auditLogService;

